import React, { useEffect, useState } from 'react';
import { emailAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Mail, Loader2, Send, RefreshCw, Save, Edit3, Key } from 'lucide-react';

const EmailConfig = () => {
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);
  const [editingTpl, setEditingTpl] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [c, t] = await Promise.all([emailAPI.getConfig(), emailAPI.listTemplates()]);
      setCfg(c.data);
      setTemplates(t.data);
    } catch (e) { toast.error('Failed to load email config'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Build payload — only send secrets if user typed them (won't show '***' value)
      const payload = {
        mode: cfg.mode || 'smtp',
        enabled: !!cfg.enabled,
        from_email: cfg.from_email || '',
        from_name: cfg.from_name || '',
        smtp_host: cfg.smtp_host || '',
        smtp_port: parseInt(cfg.smtp_port || 587),
        smtp_user: cfg.smtp_user || '',
        smtp_use_tls: cfg.smtp_use_tls !== false,
        enabled_events: cfg.enabled_events || {},
      };
      if (cfg.sendgrid_api_key && !cfg.sendgrid_api_key.includes('***')) payload.sendgrid_api_key = cfg.sendgrid_api_key;
      if (cfg.smtp_password && cfg.smtp_password !== '***') payload.smtp_password = cfg.smtp_password;
      const r = await emailAPI.updateConfig(payload);
      setCfg(r.data);
      toast.success('Configuration saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testTo) { toast.error('Enter recipient email'); return; }
    setTesting(true);
    try {
      const r = await emailAPI.sendTest(testTo);
      toast.success(r.data.message || 'Test sent');
      setTestOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Test failed'); }
    finally { setTesting(false); }
  };

  const saveTemplate = async () => {
    if (!editingTpl) return;
    try {
      await emailAPI.updateTemplate({
        event_key: editingTpl.event_key,
        subject: editingTpl.subject,
        body_html: editingTpl.body_html,
      });
      toast.success('Template saved');
      setEditingTpl(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const resetTemplate = async (eventKey) => {
    if (!window.confirm('Reset this template to default?')) return;
    try {
      await emailAPI.resetTemplate(eventKey);
      toast.success('Reset to default');
      load();
    } catch (e) { toast.error('Reset failed'); }
  };

  if (loading || !cfg) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="email-config-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Mail />Email Configuration</h1>
          <p className="text-muted-foreground">Configure SMTP or SendGrid for outgoing transactional emails</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={cfg.enabled ? 'default' : 'outline'} data-testid="email-status-badge">
            {cfg.enabled ? 'Enabled' : 'Disabled (mock)'}
          </Badge>
          <Button variant="outline" onClick={() => setTestOpen(true)} disabled={!cfg.enabled} data-testid="send-test-btn">
            <Send size={14} className="mr-2" />Send Test
          </Button>
          <Button onClick={save} disabled={saving} data-testid="save-config-btn">
            {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Save size={14} className="mr-2" />}Save
          </Button>
        </div>
      </div>

      <Tabs defaultValue="connection">
        <TabsList>
          <TabsTrigger value="connection" data-testid="tab-connection">Connection</TabsTrigger>
          <TabsTrigger value="events" data-testid="tab-events">Events ({Object.values(cfg.enabled_events || {}).filter(Boolean).length}/{templates.length})</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates ({templates.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="connection">
          <Card>
            <CardHeader><CardTitle className="text-base">Provider</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30">
                <div>
                  <Label className="text-base">Enable email sending</Label>
                  <p className="text-xs text-muted-foreground">When off, emails are logged but not sent (mock mode)</p>
                </div>
                <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="enable-email-switch" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Provider mode</Label>
                  <Select value={cfg.mode || 'smtp'} onValueChange={(v) => setCfg({ ...cfg, mode: v })}>
                    <SelectTrigger data-testid="provider-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="smtp">Generic SMTP (Gmail, Outlook, Zoho...)</SelectItem>
                      <SelectItem value="sendgrid">SendGrid (REST API)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>From Email *</Label>
                  <Input value={cfg.from_email || ''} onChange={(e) => setCfg({ ...cfg, from_email: e.target.value })} placeholder="hr@yourcompany.com" data-testid="from-email" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>From Name</Label>
                  <Input value={cfg.from_name || ''} onChange={(e) => setCfg({ ...cfg, from_name: e.target.value })} placeholder="Pride Global HR" />
                </div>
              </div>

              {cfg.mode === 'sendgrid' ? (
                <Card className="bg-muted/30">
                  <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Key size={14} />SendGrid</CardTitle></CardHeader>
                  <CardContent>
                    <Label>SendGrid API Key {cfg.has_sendgrid_api_key && <Badge variant="outline" className="ml-2">Configured</Badge>}</Label>
                    <Input
                      type="password"
                      placeholder={cfg.has_sendgrid_api_key ? cfg.sendgrid_api_key : 'SG.xxxxxxxxxxx'}
                      onChange={(e) => setCfg({ ...cfg, sendgrid_api_key: e.target.value })}
                      data-testid="sendgrid-key"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Get from app.sendgrid.com → Settings → API Keys (Full Access)</p>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-muted/30">
                  <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Key size={14} />SMTP Server</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="col-span-2">
                        <Label>Host *</Label>
                        <Input value={cfg.smtp_host || ''} onChange={(e) => setCfg({ ...cfg, smtp_host: e.target.value })} placeholder="smtp.gmail.com" data-testid="smtp-host" />
                      </div>
                      <div>
                        <Label>Port</Label>
                        <Input type="number" value={cfg.smtp_port || 587} onChange={(e) => setCfg({ ...cfg, smtp_port: e.target.value })} data-testid="smtp-port" />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Username</Label>
                        <Input value={cfg.smtp_user || ''} onChange={(e) => setCfg({ ...cfg, smtp_user: e.target.value })} data-testid="smtp-user" />
                      </div>
                      <div>
                        <Label>Password {cfg.has_smtp_password && <Badge variant="outline" className="ml-2">Configured</Badge>}</Label>
                        <Input type="password" placeholder={cfg.has_smtp_password ? '***' : 'enter to update'} onChange={(e) => setCfg({ ...cfg, smtp_password: e.target.value })} data-testid="smtp-password" />
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={cfg.smtp_use_tls !== false} onCheckedChange={(v) => setCfg({ ...cfg, smtp_use_tls: v })} />
                      <Label className="text-sm">Use STARTTLS (recommended for ports 587/25)</Label>
                    </div>
                  </CardContent>
                </Card>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="events">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Per-Event Toggles</CardTitle>
              <p className="text-xs text-muted-foreground">Disable specific events without changing templates. Disabled events are logged only.</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {templates.map(t => (
                  <div key={t.event_key} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`event-toggle-${t.event_key}`}>
                    <div>
                      <p className="font-medium">{t.label}</p>
                      <p className="text-xs text-muted-foreground">{t.event_key}</p>
                    </div>
                    <Switch
                      checked={cfg.enabled_events?.[t.event_key] !== false}
                      onCheckedChange={(v) => setCfg({ ...cfg, enabled_events: { ...(cfg.enabled_events || {}), [t.event_key]: v } })}
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Email Templates</CardTitle>
              <p className="text-xs text-muted-foreground">Use <code className="bg-muted px-1 rounded">{'{{variable}}'}</code> for placeholders (e.g. {'{{first_name}}'}, {'{{leave_dates}}'}).</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {templates.map(t => (
                  <div key={t.event_key} className="p-3 border rounded-lg" data-testid={`template-row-${t.event_key}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium">{t.label} {t.is_customized && <Badge variant="default" className="ml-2 text-xs">customized</Badge>}</p>
                        <p className="text-xs text-muted-foreground font-mono">{t.event_key}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => setEditingTpl({ ...t })} data-testid={`edit-template-${t.event_key}`}><Edit3 size={14} /></Button>
                        {t.is_customized && <Button size="sm" variant="outline" onClick={() => resetTemplate(t.event_key)} title="Reset to default"><RefreshCw size={14} /></Button>}
                      </div>
                    </div>
                    <p className="text-sm font-medium">Subject: <span className="font-normal text-muted-foreground">{t.subject}</span></p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Test email dialog */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send Test Email</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Recipient</Label>
            <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" data-testid="test-to-input" />
            <p className="text-xs text-muted-foreground">Uses the saved configuration. Make sure you've saved your changes first.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestOpen(false)}>Cancel</Button>
            <Button onClick={sendTest} disabled={testing} data-testid="confirm-send-test"><Send size={14} className="mr-2" />{testing ? 'Sending...' : 'Send'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit template dialog */}
      <Dialog open={!!editingTpl} onOpenChange={(o) => !o && setEditingTpl(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Edit Template: {editingTpl?.label}</DialogTitle></DialogHeader>
          {editingTpl && (
            <div className="space-y-3">
              <div>
                <Label>Subject</Label>
                <Input value={editingTpl.subject} onChange={(e) => setEditingTpl({ ...editingTpl, subject: e.target.value })} data-testid="edit-template-subject" />
              </div>
              <div>
                <Label>Body (HTML)</Label>
                <Textarea
                  value={editingTpl.body_html}
                  onChange={(e) => setEditingTpl({ ...editingTpl, body_html: e.target.value })}
                  rows={10}
                  className="font-mono text-xs"
                  data-testid="edit-template-body"
                />
                <p className="text-xs text-muted-foreground mt-1">Common variables: {'{{first_name}}, {{last_name}}, {{company_name}}'}, plus event-specific ones (see default below).</p>
              </div>
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">Default subject &amp; body</summary>
                <div className="mt-2 p-2 bg-muted/50 rounded">
                  <p><strong>Subject:</strong> {editingTpl.default_subject}</p>
                  <p className="mt-1"><strong>Body:</strong></p>
                  <pre className="whitespace-pre-wrap text-xs">{editingTpl.default_body_html}</pre>
                </div>
              </details>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTpl(null)}>Cancel</Button>
            <Button onClick={saveTemplate} data-testid="save-template-btn"><Save size={14} className="mr-2" />Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmailConfig;
